import { useEffect, useState } from "react";
import { api } from "../../lib/api.ts";
import { Icon } from "../../lib/icons.tsx";
import type { InterestCluster } from "@shared/interest.ts";

interface Test {
  id: string; testName: string; audience?: string | null; creative?: string | null; offer?: string | null;
  budget?: string | null; objective?: string | null; hypothesis?: string | null; primaryKpi?: string | null;
  spend?: number | null; lead?: number | null; cpl?: number | null; leadQuality?: string | null;
  sale?: number | null; revenue?: number | null; roas?: number | null; status?: string | null; learning?: string | null;
}

const STATUS: Record<string, string> = {
  planned: "mh-pill--neutral", running: "mh-pill--testing", winner: "mh-pill--winner",
  watch: "mh-pill--watch", problem: "mh-pill--problem", killed: "mh-pill--problem",
};
const STATUS_LABEL: Record<string, string> = {
  planned: "วางแผน", running: "กำลังรัน", winner: "Winner", watch: "เฝ้าดู", problem: "มีปัญหา", killed: "ปิดแล้ว",
};

export function TestingMatrix({ brandId, clusters }: { brandId: string | null; clusters: InterestCluster[] }) {
  const [tests, setTests] = useState<Test[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, [brandId]);
  async function load() {
    if (!brandId) return;
    const r = await api.get<{ tests: Test[] }>(`/interest/tests/list?brandId=${brandId}`);
    setTests(r.tests);
  }

  async function addTest(fromCluster?: InterestCluster) {
    if (!brandId) return;
    setLoading(true);
    try {
      const r = await api.post<{ test: Test }>("/interest/tests", {
        brandId,
        testName: fromCluster ? `เทสต์: ${fromCluster.name}` : "Test ใหม่",
        audience: fromCluster?.name ?? "",
        offer: fromCluster?.offerAngle ?? "",
        creative: fromCluster?.creativeAngle ?? "",
        budget: fromCluster?.budgetSuggestion ?? "",
        objective: fromCluster?.testObjective ?? "",
        hypothesis: fromCluster?.hypothesis ?? "",
        primaryKpi: fromCluster?.kpi ?? "",
        status: "planned",
      });
      setTests((t) => [r.test, ...t]);
      setEditing(r.test.id);
    } finally { setLoading(false); }
  }

  async function save(id: string, patch: Partial<Test>) {
    const r = await api.put<{ test: Test }>(`/interest/tests/${id}`, patch);
    setTests((ts) => ts.map((t) => (t.id === id ? r.test : t)));
  }

  if (!brandId) return <div className="empty"><p className="mh-sec">เลือกแบรนด์ก่อนเพื่อจัดการ Testing Matrix</p></div>;

  return (
    <div>
      <div className="mh-card card-pad" style={{ marginBottom: 16, background: "var(--blue-050)", borderColor: "var(--blue-100)" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <Icon name="Scale" size={18} />
          <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            <b>กติกาการทดสอบ (Control Variable):</b> ถ้าทดสอบ <b>Audience</b> ให้คุม Creative ให้เหมือนกัน · ถ้าทดสอบ <b>Creative</b> ให้คุม Audience ·
            ถ้าเปลี่ยน Audience + Creative + Offer พร้อมกัน ผลจะ <b style={{ color: "var(--red-600)" }}>อ่านไม่ออกว่าอะไรได้ผล (Attribution ไม่ชัด)</b>
          </div>
        </div>
      </div>

      <div className="mh-spread" style={{ marginBottom: 14 }}>
        <h3 style={{ fontSize: 15 }}>Testing Matrix ({tests.length})</h3>
        <button className="mh-btn mh-btn--primary mh-btn--sm" onClick={() => addTest()} disabled={loading}><Icon name="Plus" size={16} /> เพิ่มเทสต์</button>
      </div>

      {clusters.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {clusters.map((cl) => (
            <button key={cl.ref} className="mh-btn mh-btn--subtle mh-btn--sm" onClick={() => addTest(cl)}>
              <Icon name="Plus" size={14} /> จาก {cl.name.slice(0, 24)}
            </button>
          ))}
        </div>
      )}

      {tests.length === 0 ? (
        <div className="empty"><div className="empty__icon"><Icon name="Radar" size={28} /></div><p className="mh-sec">ยังไม่มีเทสต์ — สร้างจาก Cluster หรือกด "เพิ่มเทสต์"</p></div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {tests.map((t) => (
            <div className="mh-card card-pad" key={t.id}>
              <div className="mh-spread">
                <div style={{ fontWeight: 700 }}>{t.testName}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span className={"mh-pill " + (STATUS[t.status || "planned"])}>{STATUS_LABEL[t.status || "planned"]}</span>
                  <button className="mh-btn mh-btn--ghost mh-btn--sm" onClick={() => setEditing(editing === t.id ? null : t.id)}>
                    <Icon name="Pencil" size={14} /> {editing === t.id ? "ปิด" : "แก้ไข"}
                  </button>
                </div>
              </div>

              {/* summary row */}
              <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(110px,1fr))", marginTop: 12 }}>
                <MiniStat k="Spend" v={t.spend != null ? `฿${t.spend}` : "—"} />
                <MiniStat k="Lead" v={t.lead ?? "—"} />
                <MiniStat k="CPL" v={t.cpl != null ? `฿${t.cpl}` : "—"} />
                <MiniStat k="Sale" v={t.sale ?? "—"} />
                <MiniStat k="ROAS" v={t.roas ?? "—"} />
                <MiniStat k="คุณภาพ Lead" v={t.leadQuality || "—"} />
              </div>

              {editing === t.id && <EditForm test={t} onSave={(patch) => save(t.id, patch)} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ k, v }: { k: string; v: any }) {
  return (
    <div style={{ background: "var(--gray-050)", borderRadius: 8, padding: "7px 10px", textAlign: "center" }}>
      <div className="mh-muted" style={{ fontSize: 11 }}>{k}</div>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{v}</div>
    </div>
  );
}

function EditForm({ test, onSave }: { test: Test; onSave: (p: Partial<Test>) => void }) {
  const [f, setF] = useState<Test>(test);
  const set = (k: keyof Test, v: any) => setF((p) => ({ ...p, [k]: v }));
  const num = (v: string) => (v === "" ? null : Number(v));

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
      <div className="form-grid">
        <F label="ชื่อเทสต์"><input className="mh-input" value={f.testName || ""} onChange={(e) => set("testName", e.target.value)} /></F>
        <F label="สถานะ">
          <select className="mh-select" value={f.status || "planned"} onChange={(e) => set("status", e.target.value)}>
            {Object.keys(STATUS_LABEL).map((k) => <option key={k} value={k}>{STATUS_LABEL[k]}</option>)}
          </select>
        </F>
        <F label="Audience"><input className="mh-input" value={f.audience || ""} onChange={(e) => set("audience", e.target.value)} /></F>
        <F label="Creative"><input className="mh-input" value={f.creative || ""} onChange={(e) => set("creative", e.target.value)} /></F>
        <F label="Offer"><input className="mh-input" value={f.offer || ""} onChange={(e) => set("offer", e.target.value)} /></F>
        <F label="งบ (Budget)"><input className="mh-input" value={f.budget || ""} onChange={(e) => set("budget", e.target.value)} /></F>
        <F label="Hypothesis" full><textarea className="mh-textarea" style={{ minHeight: 52 }} value={f.hypothesis || ""} onChange={(e) => set("hypothesis", e.target.value)} /></F>
      </div>

      <div style={{ margin: "14px 0 8px", fontWeight: 700, fontSize: 13.5, color: "var(--blue-600)" }}>📊 ผลลัพธ์จริง (กรอกหลังยิงแอด)</div>
      <div className="form-grid">
        <F label="Spend (฿)"><input className="mh-input" type="number" value={f.spend ?? ""} onChange={(e) => set("spend", num(e.target.value))} /></F>
        <F label="Lead"><input className="mh-input" type="number" value={f.lead ?? ""} onChange={(e) => set("lead", num(e.target.value))} /></F>
        <F label="CPL (฿)"><input className="mh-input" type="number" value={f.cpl ?? ""} onChange={(e) => set("cpl", num(e.target.value))} /></F>
        <F label="คุณภาพ Lead"><input className="mh-input" value={f.leadQuality || ""} onChange={(e) => set("leadQuality", e.target.value)} placeholder="ดี/กลาง/แย่" /></F>
        <F label="Sale"><input className="mh-input" type="number" value={f.sale ?? ""} onChange={(e) => set("sale", num(e.target.value))} /></F>
        <F label="Revenue (฿)"><input className="mh-input" type="number" value={f.revenue ?? ""} onChange={(e) => set("revenue", num(e.target.value))} /></F>
        <F label="ROAS"><input className="mh-input" type="number" value={f.roas ?? ""} onChange={(e) => set("roas", num(e.target.value))} /></F>
        <F label="สรุป Learning" full><textarea className="mh-textarea" style={{ minHeight: 52 }} value={f.learning || ""} onChange={(e) => set("learning", e.target.value)} placeholder="อะไรได้ผล/ไม่ได้ผล เพราะอะไร ทดสอบอะไรต่อ" /></F>
      </div>

      <div style={{ marginTop: 12 }}>
        <button className="mh-btn mh-btn--primary" onClick={() => onSave(f)}><Icon name="Save" size={16} /> บันทึกเทสต์</button>
      </div>
    </div>
  );
}

function F({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className="mh-field" style={{ margin: 0, gridColumn: full ? "1 / -1" : undefined }}>
      <label className="mh-label">{label}</label>
      {children}
    </div>
  );
}
